import { ExploreTestID } from "@/constants/TestID";
import EventAttendeesList from "@/ui/components/schedule/EventCardAttendeesList";
import type { AvatarCardProps } from "@/ui/core";
import { FAIconName, Icon, Text, TextTag } from "@/ui/core";
import { BaseCard, HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { getHostsLabel } from "@/utils/groupClassV2.util";
import { joinTestIDs, pluralize } from "@/utils/string.util";

interface GroupClassSessionCardProps extends ViewWithTestID {
  hosts?: AvatarCardProps[];
  // open-ended series with no fixed session count: shows an "Ongoing" tag instead of a count
  isOngoing?: boolean;
  // selection mode (e.g. recommended modal): highlights the card and hides the chevron,
  // which otherwise signals "opens details"
  isSelected?: boolean;
  onPress: () => void;
  sessionCount: number;
  shouldHideChevron?: boolean;
  timeRange: string;
  title: string;
}

const GroupClassSessionCard = ({
  hosts,
  isOngoing,
  isSelected,
  onPress,
  sessionCount,
  shouldHideChevron,
  testID,
  timeRange,
  title,
}: GroupClassSessionCardProps) => {
  const baseTestID = joinTestIDs(testID, ExploreTestID.GroupClassSessionCard);

  const hostsLabel = getHostsLabel(hosts);

  return (
    <BaseCard isSelected={isSelected} onPress={onPress} testID={baseTestID}>
      <VStack gap={12}>
        <HStack alignItems="center" gap={12} justifyContent="space-between">
          <VStack flex={1} flexShrink={1} gap={4}>
            <Text
              testID={joinTestIDs(baseTestID, "title")}
              variant="bodyMedium"
            >
              {title}
            </Text>
            <Text
              color={ColorToken.NeutralSecondary}
              testID={joinTestIDs(
                baseTestID,
                ExploreTestID.GroupClassSessionCardTimeRange,
              )}
              variant="caption"
            >
              {timeRange}
            </Text>
          </VStack>
          <HStack alignItems="center" flexShrink={0} gap={12}>
            {sessionCount > 0 && (
              <TextTag
                testID={joinTestIDs(
                  baseTestID,
                  ExploreTestID.GroupClassSessionCardSessionsBadge,
                )}
                text={`${sessionCount} session${pluralize({ itemCount: sessionCount })}`}
                variant="selection"
              />
            )}
            {isOngoing && (
              <TextTag
                testID={joinTestIDs(
                  baseTestID,
                  ExploreTestID.GroupClassSessionCardOngoingBadge,
                )}
                text="Ongoing"
                variant="selection"
              />
            )}
            {!shouldHideChevron && (
              <Icon
                color={ColorToken.NeutralPrimary}
                name={FAIconName.ChevronRight}
                size="sm"
                testID={joinTestIDs(
                  baseTestID,
                  ExploreTestID.GroupClassSessionCardChevron,
                )}
              />
            )}
          </HStack>
        </HStack>
        {hostsLabel && (
          <HStack alignItems="center" gap={12}>
            <EventAttendeesList attendees={hosts} />
            <Text
              color={ColorToken.NeutralSecondary}
              flexShrink={1}
              testID={joinTestIDs(
                baseTestID,
                ExploreTestID.GroupClassSessionCardHostsLabel,
              )}
              variant="caption"
            >
              {hostsLabel}
            </Text>
          </HStack>
        )}
      </VStack>
    </BaseCard>
  );
};

export type { GroupClassSessionCardProps };
export default GroupClassSessionCard;
